import { useEffect, useRef } from 'react'
import { getAuthToken } from 'library/auth'
import { server } from 'utils/constants'

const RECONNECT_DELAY_MS = 1000

export function useConversationChannel(conversationId, onMessage) {
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage

  useEffect(() => {
    if (!conversationId) return

    let ws: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let intentionallyClosed = false

    const identifier = JSON.stringify({
      channel: 'PatientPortal::ConversationChannel',
      conversation_id: conversationId,
    })

    const getWsUrl = () => {
      const { origin } = new URL(server)
      return `${origin.replace(/^http/, 'ws')}/cable?token=${getAuthToken()}`
    }

    const scheduleReconnect = () => {
      if (intentionallyClosed || reconnectTimer) return
      reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS)
    }

    function connect() {
      if (intentionallyClosed) return

      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }

      if (ws && ws.readyState !== WebSocket.CLOSED) {
        ws.onclose = null
        ws.close()
      }
      ws = new WebSocket(getWsUrl())

      ws.onopen = () => {
        ws?.send(JSON.stringify({ command: 'subscribe', identifier }))
      }

      ws.onmessage = (event) => {
        const parsed = JSON.parse(event.data)
        console.log(parsed) // logger

        if (parsed?.message?.type === 'message.created') {
          onMessageRef.current(parsed.message.data.data)
        }
      }

      ws.onclose = () => {
        scheduleReconnect()
      }

      ws.onerror = () => {
        ws?.close()
      }
    }

    const reconnectOnWake = () => {
      if (document.visibilityState === 'hidden') return
      if (ws?.readyState === WebSocket.CONNECTING) return
      if (ws?.readyState === WebSocket.OPEN) return

      connect()
    }

    connect()
    window.addEventListener('online', reconnectOnWake)
    window.addEventListener('focus', reconnectOnWake)
    document.addEventListener('visibilitychange', reconnectOnWake)

    return () => {
      intentionallyClosed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      window.removeEventListener('online', reconnectOnWake)
      window.removeEventListener('focus', reconnectOnWake)
      document.removeEventListener('visibilitychange', reconnectOnWake)
      ws?.close()
    }
  }, [conversationId])
}
